/**
 * Verrouillage des documents de vente enregistrés.
 *
 * Règle légale tunisienne (numérotation séquentielle sans trou) :
 *   • Une FACTURE enregistrée n'est JAMAIS modifiable. Toute correction passe
 *     par un avoir (ou la suppression, qui reste gérée séparément).
 *   • Un BON DE LIVRAISON déjà facturé (is_invoiced) n'est plus modifiable :
 *     son contenu est figé dans la facture qui le référence. Il se déverrouille
 *     automatiquement si cette facture est supprimée (le repository remet
 *     is_invoiced à false quand plus aucune facture ne le référence).
 *   • Un AVOIR enregistré n'est plus modifiable : ses lignes ont pu remettre
 *     le stock en place et/ou être figées dans une facture. Le réécrire
 *     désynchroniserait ces effets historiques.
 *
 * Ces fonctions s'appliquent aux documents PERSISTÉS (déjà enregistrés). La
 * création — y compris la reprise idempotente d'une création interrompue —
 * n'est pas concernée.
 */

/** Types de documents de vente (structurellement identique à SalesDocType
 *  de @erp/data-access/interfaces — redéclaré ici car le domaine ne dépend
 *  d'aucune couche d'accès aux données). */
export type SalesEditabilityDocType = 'quote' | 'order' | 'delivery' | 'invoice' | 'credit_note';

export type SalesDocumentLockReason =
  | 'invoice_saved'
  | 'delivery_invoiced'
  | 'credit_note_saved';

export type SalesDocumentEditability =
  | { editable: true; reason?: undefined }
  | { editable: false; reason: SalesDocumentLockReason };

export interface SalesEditabilityInput {
  docType: SalesEditabilityDocType;
  /** Marqueur `is_invoiced` (BL et commandes). Absent/false = non facturé. */
  isInvoiced?: boolean | null;
}

/**
 * Éditabilité d'un document de vente ENREGISTRÉ.
 * Devis et commandes restent modifiables.
 */
export function getSalesDocumentEditability(doc: SalesEditabilityInput): SalesDocumentEditability {
  if (doc.docType === 'invoice') {
    return { editable: false, reason: 'invoice_saved' };
  }
  if (doc.docType === 'credit_note') {
    return { editable: false, reason: 'credit_note_saved' };
  }
  if (doc.docType === 'delivery' && doc.isInvoiced === true) {
    return { editable: false, reason: 'delivery_invoiced' };
  }
  return { editable: true };
}

/** Raccourci booléen de getSalesDocumentEditability. */
export function isSalesDocumentEditable(doc: SalesEditabilityInput): boolean {
  return getSalesDocumentEditability(doc).editable;
}

/**
 * Ce document peut-il être SUPPRIMÉ ?
 *
 * À ne pas confondre avec l'éditabilité : une facture enregistrée n'est jamais
 * modifiable, mais elle est supprimable depuis 2026-07-26 (décision explicite
 * de Khalil, qui prime sur la règle 4 de CLAUDE.md — la séquence peut donc se
 * retrouver trouée, ce qu'un contrôle fiscal tunisien peut relever ; le réglage
 * `reuseDeletedNumbers` décide si le numéro libéré revient à la facture
 * suivante).
 *
 * Un AVOIR reste indestructible : il matérialise l'annulation d'une facture, et
 * le supprimer ferait réapparaître une créance déjà annulée — sans qu'aucune
 * trace n'explique pourquoi. Corriger un avoir de trop se fait par une nouvelle
 * facture, pas en effaçant l'annulation.
 *
 * Cette règle décide ce que l'écran PROPOSE ; qui a le droit de le faire est
 * une question distincte (permission ventes:suppression).
 */
export function canDeleteSalesDocument(doc: SalesEditabilityInput): boolean {
  return doc.docType !== 'credit_note';
}

/** Cibles de transformation, ventes ET achats confondues — la règle ci-dessous
 *  est la même des deux côtés (BL → facture, BR → facture d'achat). */
export type TransformTarget =
  | 'quote' | 'order' | 'delivery' | 'reception' | 'invoice' | 'credit_note';

/**
 * Ce document source peut-il encore être transformé en `target` ?
 *
 * UNE SEULE règle, et elle vaut pour les deux familles : **une source déjà
 * facturée ne se refacture pas**. Un BL facturé, un BR facturé, ont cédé leur
 * contenu ET leur créance à la facture qui les référence ; en refaire une
 * seconde facturerait deux fois la même marchandise.
 *
 * Le repository refuse déjà (assertParentsNotAlreadyInvoiced, des deux côtés),
 * mais tout en aval : l'utilisateur ouvrait « Transformer en Facture » sur un
 * document marqué Facturé, ressaisissait tout — numéro fournisseur compris,
 * côté achats — et ne l'apprenait qu'à l'enregistrement. Les écrans posent donc
 * la même question AVANT, là où le marqueur `is_invoiced` est affiché juste à
 * côté.
 *
 * Pour un avoir, un BL déjà facturé n'est plus la bonne origine : l'avoir
 * doit partir de la facture et rester une pièce séparée. Cela évite de créer
 * après coup un avoir de BL que la facture existante ne pourra jamais embarquer.
 */
export function canTransformSourceInto(
  source: { docType?: SalesEditabilityDocType | 'reception'; isInvoiced?: boolean | null },
  target: TransformTarget,
): boolean {
  if (target === 'invoice') return source.isInvoiced !== true;
  if (target === 'credit_note' && source.docType === 'delivery') {
    return source.isInvoiced !== true;
  }
  return true;
}
